import React, { useMemo } from 'react';
import { Typography, Paper } from '@mui/material';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { FileItem } from './types';
import { fileCardModels } from './selectionModel';
import { analysisTargetIds } from './analysisTargets';
import { useTranslation } from '../../i18n';
import { useFlightAnalysisResults } from '../../hooks/useFlightAnalysisResults';
import DraggableFileCard from './DraggableFileCard';
import SelectableGrid from './SelectableGrid';

interface FileGridProps {
  files: FileItem[];
  loading: boolean;
  /** Entry ids of the current selection (single-click selects) — STD-029 R-2. */
  selectedIds: ReadonlySet<string>;
  /** Entry ids currently cut to the clipboard — rendered dimmed until paste/clear. */
  cutIds: ReadonlySet<string>;
  /** Single-click — apply selection, reading Ctrl/Cmd/Shift off the event. */
  onItemClick: (file: FileItem, event: React.MouseEvent<HTMLElement>) => void;
  /** Double-click — open: navigate into a folder or download a file. */
  onItemOpen: (file: FileItem) => void;
  /** Click on empty grid space — clear the selection. */
  onBackgroundClick: () => void;
  /** Marquee finished — replace selection with the intersected entry ids. */
  onMarqueeSelect: (ids: string[]) => void;
  /**
   * Is a given folder a valid drop target for the current drag?
   *
   * Takes the ENTRY, not a key: the guard behind it is still path math (D-4),
   * and passing the entry is what keeps the path read off the row instead of
   * parsed out of an identity.
   */
  canDropOnFolder: (folder: FileItem) => boolean;
  onContextMenu: (event: React.MouseEvent<HTMLElement>, file: FileItem) => void;
}

const FileGrid: React.FC<FileGridProps> = ({
  files,
  loading,
  selectedIds,
  cutIds,
  onItemClick,
  onItemOpen,
  onBackgroundClick,
  onMarqueeSelect,
  canDropOnFolder,
  onContextMenu,
}) => {
  const { t } = useTranslation();

  // Ask for verdicts by object id, not by the display key (#1326). The rule is
  // `analysisTargets.ts` — pure, and unit-tested there, because nothing else in
  // this app can catch a wrong filter.
  const analysisObjectIds = useMemo(() => analysisTargetIds(files), [files]);
  const { data: analysisResults } = useFlightAnalysisResults(analysisObjectIds);

  // Every identity decision for the cards, made once in tested code (STD-029
  // R-2). This component deliberately does not call `entryId` itself: an
  // `entryId(file)` written inline in JSX is unreachable from a unit test and
  // indistinguishable to `tsc` from a revert to `file.key`.
  const cards = useMemo(
    () => fileCardModels(files, selectedIds, cutIds),
    [files, selectedIds, cutIds]
  );

  if (loading) {
    return null;
  }

  if (files.length === 0) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.02),
        }}
      >
        <Typography color="textSecondary">{t('fileManagement.noFilesInDirectory')}</Typography>
      </Paper>
    );
  }

  return (
    <SelectableGrid
      data-testid="file-grid"
      onBackgroundClick={onBackgroundClick}
      onMarqueeSelect={onMarqueeSelect}
    >
      {cards.map((card) => (
        <DraggableFileCard
          // React's reconciliation key is the entry's identity too: keyed on
          // the display path, two rows at one path collide and React reuses
          // one card's state (selection ring, drag handle) for the other.
          key={card.id}
          entryId={card.id}
          file={card.file}
          selected={card.selected}
          cut={card.cut}
          verdict={
            card.file.object_id
              ? analysisResults.get(card.file.object_id)?.overall_verdict
              : undefined
          }
          canDropHere={canDropOnFolder(card.file)}
          onItemClick={onItemClick}
          onItemOpen={onItemOpen}
          onContextMenu={onContextMenu}
        />
      ))}
    </SelectableGrid>
  );
};

export default FileGrid;
